import { useCallback, useEffect, useRef, useState } from "react";
import { settingsApi, type AppSettings } from "@/lib/settings-api.service";

/**
 * Fail-safe default: matches the backend's default and the pre-existing
 * behaviour (print dialog before every "Tiếp nhận"). Used both as the
 * pre-fetch state and as what stays on screen if the fetch fails — a broken
 * `/v1/settings` call must never silently disable printing.
 */
const DEFAULT_SETTINGS: AppSettings = {
	allowPrintBeforeCompletion: true,
};

/**
 * Global app settings, revalidating like `usePapers`: `loading` only means
 * "nothing fetched yet", so a background refetch never blanks the value a
 * component is currently reading.
 *
 * On fetch failure `data` is deliberately left untouched (defaulting to
 * `DEFAULT_SETTINGS` on first load) — see the module doc on
 * `DEFAULT_SETTINGS` for why.
 */
export function useAppSettings() {
	const [data, setData] = useState<AppSettings>(DEFAULT_SETTINGS);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	// Survives re-renders without triggering one; only the FIRST load blanks out.
	const hasLoaded = useRef(false);

	const fetcher = useCallback(() => settingsApi.get(), []);

	const refetch = useCallback(async () => {
		if (!hasLoaded.current) setLoading(true);
		setError(null);
		try {
			setData(await fetcher());
			hasLoaded.current = true;
		} catch (err) {
			setError(err instanceof Error ? err.message : "Không thể tải cài đặt hệ thống");
		} finally {
			setLoading(false);
		}
	}, [fetcher]);

	useEffect(() => {
		refetch();
	}, [refetch]);

	return { settings: data, setSettings: setData, loading, error, refetch };
}
